import { useCallback, useMemo, useState } from "react";

import type { BillingStatus } from "@/lib/gql/__generated__/graphql";

type UseRecurringTicketStateProps = {
	billingStatus: BillingStatus;
};

type UseRecurringTicketStateReturn = {
	isRecurringPurchase: boolean;
	defaultRecurringProBillingTicketID: string | undefined;
	canShowNextBillingDtPro: boolean;
	isStopRecurringProBilling: boolean;
	onUpdateIsStopRecurringProBilling: () => void;
	onResetIsStopRecurringProBilling: () => void;
};

export const useRecurringTicketState = ({
	billingStatus,
}: UseRecurringTicketStateProps): UseRecurringTicketStateReturn => {
	/** 定期購入しているかどうか */
	const isRecurringPurchase = useMemo(
		() => !billingStatus.isStopRecurringProBilling,
		[billingStatus.isStopRecurringProBilling],
	);

	/** 定期購入しているチケットのID */
	const defaultRecurringProBillingTicketID = useMemo(() => {
		if (!isRecurringPurchase || !billingStatus.defaultProBillingTicketID) {
			return undefined;
		}
		return billingStatus.defaultProBillingTicketID;
	}, [billingStatus.defaultProBillingTicketID, isRecurringPurchase]);

	/** 次回のご請求日が表示できるかどうか */
	const canShowNextBillingDtPro = useMemo(
		() => !!billingStatus.defaultProBillingTicketName && !!billingStatus.nextBillingDtPro,
		[billingStatus.defaultProBillingTicketName, billingStatus.nextBillingDtPro],
	);

	const [isStopRecurringProBilling, setIsStopRecurringProBilling] = useState(billingStatus.isStopRecurringProBilling);

	const onUpdateIsStopRecurringProBilling = useCallback(() => {
		setIsStopRecurringProBilling((prev) => !prev);
	}, []);

	const onResetIsStopRecurringProBilling = useCallback(() => {
		setIsStopRecurringProBilling(billingStatus.isStopRecurringProBilling);
	}, [billingStatus.isStopRecurringProBilling]);

	return {
		isRecurringPurchase,
		defaultRecurringProBillingTicketID,
		canShowNextBillingDtPro,
		isStopRecurringProBilling,
		onUpdateIsStopRecurringProBilling,
		onResetIsStopRecurringProBilling,
	};
};
